import { isAxiosError } from "axios";
import type { AxiosInstance, AxiosRequestConfig } from "axios";
import axios from "../config/axios";
import { createProjectApis } from "./projectCore";
import type { ProjectRequest, ProjectTransport } from "./projectCore";

function requestConfig(request: ProjectRequest): AxiosRequestConfig | undefined {
    const config: AxiosRequestConfig = {};
    if (request.query !== undefined) config.params = request.query;
    if (request.signal !== undefined) config.signal = request.signal;
    if (request.headers !== undefined) config.headers = request.headers;
    if (request.responseType !== undefined) config.responseType = request.responseType;
    return Object.keys(config).length === 0 ? undefined : config;
}

export function createAxiosProjectTransport(instance: AxiosInstance): ProjectTransport {
    return {
        async request<T>(request: ProjectRequest): Promise<T> {
            const config = requestConfig(request);
            switch (request.method) {
                case "GET": {
                    const response = config
                        ? await instance.get<T>(request.path, config)
                        : await instance.get<T>(request.path);
                    return response.data;
                }
                case "POST": {
                    const response = config
                        ? await instance.post<T>(request.path, request.body, config)
                        : await instance.post<T>(request.path, request.body);
                    return response.data;
                }
                case "PUT": {
                    const response = config
                        ? await instance.put<T>(request.path, request.body, config)
                        : await instance.put<T>(request.path, request.body);
                    return response.data;
                }
                case "PATCH": {
                    const response = config
                        ? await instance.patch<T>(request.path, request.body, config)
                        : await instance.patch<T>(request.path, request.body);
                    return response.data;
                }
                case "DELETE": {
                    const deleteConfig =
                        request.body === undefined ? config : { ...config, data: request.body };
                    const response = deleteConfig
                        ? await instance.delete<T>(request.path, deleteConfig)
                        : await instance.delete<T>(request.path);
                    return response.data;
                }
            }
            throw new Error("Unsupported project request method");
        },

        getErrorStatus(error) {
            return isAxiosError(error) ? error.response?.status : undefined;
        },
    };
}

export const projectAxiosTransport = createAxiosProjectTransport(axios);

export const {
    documents: projectDocumentApi,
    folders: projectFoldersApi,
    rfis: projectRfiApi,
    submittals: projectSubmittalApi,
    schedule: projectScheduleApi,
} = createProjectApis(projectAxiosTransport);
